import { useCallback, useEffect, useRef } from "react";
import { useMutation } from "@tanstack/react-query";

import {
  finalizePatientDraft,
  syncPatientDraft,
  type PatientDraftResponse,
} from "~/api/patientDrafts";
import { type Patient } from "~/api/patients";
import { patientToSyncPayload } from "~/shared/utils/patientToSyncPayload";
import { useFunnelStore } from "~/stores/useFunnelStore";
import { useSignUpStore } from "~/stores/useSignUpStore";

export const useDraftSync = () => {
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, []);

  const syncMutation = useMutation({
    mutationFn: ({
      sessionId,
      patientData,
    }: {
      sessionId: string;
      patientData: Record<string, unknown>;
    }) => syncPatientDraft(sessionId, patientData),
    onSuccess: (data: PatientDraftResponse) => {
      const store = useSignUpStore.getState();
      store.setDraftState({
        insuranceGroup: data.insuranceGroup,
        insuranceGroupName: data.insuranceGroupName,
        insuranceGroupResolved: data.insuranceGroupResolved,
        readyToFinalize: data.readyToFinalize,
      });
    },
  });

  const finalizeMutation = useMutation({
    mutationFn: ({
      sessionId,
      patientData,
    }: {
      sessionId: string;
      patientData: Record<string, unknown>;
    }) => finalizePatientDraft(sessionId, patientData),
  });

  const syncDraft = useCallback(
    (patient?: Patient) => {
      if (useSignUpStore.getState().appointmentBooked) return;

      const sessionId = useFunnelStore.getState().getOrCreateSession();
      const patientData = patient ?? useSignUpStore.getState().patient;
      const payload = patientToSyncPayload(patientData);

      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }

      debounceTimer.current = setTimeout(() => {
        syncMutation.mutate({ sessionId, patientData: payload });
      }, 300);
    },
    [syncMutation],
  );

  const flushDraftSync = useCallback(
    async (patient?: Patient) => {
      if (useSignUpStore.getState().appointmentBooked) return;

      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
        debounceTimer.current = null;
      }

      const sessionId = useFunnelStore.getState().getOrCreateSession();
      const patientData = patient ?? useSignUpStore.getState().patient;
      const payload = patientToSyncPayload(patientData);

      return syncMutation.mutateAsync({ sessionId, patientData: payload });
    },
    [syncMutation],
  );

  const finalizeDraft = useCallback(
    (patient?: Patient) => {
      if (useSignUpStore.getState().appointmentBooked) {
        throw new Error("Cannot finalize: appointment already booked.");
      }
      // Cancel any in-flight debounced sync so a late PATCH can't race the
      // finalize call, which now carries the authoritative payload itself.
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
        debounceTimer.current = null;
      }
      const sessionId = useFunnelStore.getState().getOrCreateSession();
      const patientData = patientToSyncPayload(
        patient ?? useSignUpStore.getState().patient,
      );
      return finalizeMutation.mutateAsync({ sessionId, patientData });
    },
    [finalizeMutation],
  );

  return {
    syncDraft,
    flushDraftSync,
    finalizeDraft,
    isSyncing: syncMutation.isPending,
    isFinalizing: finalizeMutation.isPending,
    lastSyncResponse: syncMutation.data,
  };
};
